import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MessageCircle } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { startLineOauthLink, type LineLinkMe } from '@/lib/line-link';
import { toast } from '@/stores/toast';

const DISMISS_KEY = 'line-link-banner-dismissed';

/**
 * v0.4 追加要件⑲改訂 (D-844/D-850): 初回ログイン時にもLINE連携を促す導線。
 * `GET /line/link/me` の `linked` が false である間、ホーム画面に表示する。
 *
 * ⚠️ 表示条件・再表示ポリシー (D-850): 「未連携の間は毎回表示」、「後で」は
 * `sessionStorage` でこのブラウザのセッション内のみ非表示にする (ログインし直すと再度表示される)。
 * 恒久的に隠せる手段は用意しない (全従業員の連携率を上げる目的を弱めてしまうため)。
 *
 * ⚠️ 1巡目レビューMajor反映 (design-expert): 既存の `HomeAnnouncements` (お知らせ欄) との
 * 重畳表示を避けるため、HomePage側でその後ろに配置し、最大1件のみ・低優先度の位置付けとする。
 */
export function LineLinkBanner() {
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === '1');
  const [starting, setStarting] = useState(false);

  const { data: me } = useQuery<LineLinkMe>({
    queryKey: ['line-link-me'],
    queryFn: () => apiFetch<LineLinkMe>('/line/link/me'),
    retry: false,
  });

  if (dismissed || !me?.employee_id || me.linked) return null;

  const handleStart = async () => {
    setStarting(true);
    try {
      await startLineOauthLink();
    } catch {
      toast.error('LINE連携の開始に失敗しました');
      setStarting(false);
    }
  };

  const handleLater = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div className="rounded-lg bg-surface-card shadow-card p-4 flex items-start gap-3 flex-wrap">
      <MessageCircle size={18} className="text-chrome-base shrink-0 mt-0.5" aria-hidden />
      <div className="flex-1 min-w-0 space-y-1">
        <p className="text-sm font-semibold text-text-body">LINEと連携すると、パスワードを忘れたときに自分で再設定できます</p>
        <p className="text-xs text-text-sub">
          タップするとLINEの画面に移動します。友だち追加をまだの場合は連携時にご案内します。
        </p>
      </div>
      {/* ⚠️ 2巡目実装レビューMajor反映 (design-expert): HomeAnnouncementsの「すべて見る」が同じHome画面で
          既にbrand-redを使っているため (ui-guidelines §2.2「赤は1画面1箇所」)、本バナーは
          misespark-navy(既存の非赤プライマリ配色、TimeRangeInput等で使用)を使う */}
      <div className="flex items-center gap-3 shrink-0">
        <button
          type="button"
          onClick={handleLater}
          className="min-w-[44px] text-xs text-text-sub hover:underline min-h-[44px] px-3"
        >
          後で
        </button>
        <button
          type="button"
          onClick={handleStart}
          disabled={starting}
          className="px-3 py-2 rounded bg-misespark-navy text-white text-sm hover:opacity-90 disabled:opacity-50 min-h-[44px]"
        >
          {starting ? 'LINEに移動中…' : 'LINEと連携する'}
        </button>
      </div>
    </div>
  );
}
